import { createSignal, onCleanup } from 'solid-js'
import { afterRender } from '../utils'
import type { Style } from '../../project/posterSchema'

// A text box hugs its ink, so the only floor on a measured (or line-box-computed) text box is a
// non-degenerate one: zero would break the corner drag's aspect ratio (w0/h0) and leave nothing to
// hit-test. Anything larger becomes slack that the vertical alignment has to place — a 20px floor put
// that slack under the text once the font shrank past it. Empty text is not covered by this: it has no
// ink to hug and takes hugContentMinSize's placeholder-sized default instead.
export const MIN_TEXT_BOX = 1

// ── Measurement ───────────────────────────────────────────────────────────────

let _measurer: CanvasRenderingContext2D | null = null

// Web fonts (Google Fonts, injected as <link rel=stylesheet display=swap>) finish loading after the
// first paint, so the canvas measurer below reports *fallback*-font metrics until the real font
// arrives. That mismeasurement makes wrapText / fitFontSize wrap or shrink text that actually fits at
// the real font, and — because the canvas measurer is not reactive — the wrong layout sticks once
// computed. This epoch ticks each time the document's fonts finish loading; measureWidth reads it, so
// every memo that wraps/fits text re-runs against the correct metrics once the font is ready.
const [fontEpoch, setFontEpoch] = createSignal(0)
export { fontEpoch }

let _watchingFonts = false
export function watchFontLoads(): void {
	if (_watchingFonts || typeof document === 'undefined' || !document.fonts) return
	_watchingFonts = true
	// Each font load both re-wraps text (fontEpoch) and re-runs every text element's geometry fit
	// (markGeometryReady). The fit has to track font loads the same way wrapping does: document.fonts.
	// ready can resolve *before* the poster's <link>-injected web fonts have registered (so the first
	// fit measures fallback metrics), and the real font then arrives via a later loadingdone — at which
	// point the box must be re-fitted against the now-correct metrics, not left at the fallback fit.
	document.fonts.addEventListener('loadingdone', () => { notifyFontLoaded() })
	void document.fonts.ready.then(() => { notifyFontLoaded() })
}

// A font became usable: re-wrap text (fontEpoch) and re-run every text element's fit
// (markGeometryReady). Exported because the two document-level triggers above are not exhaustive —
// a face resolved through an explicit document.fonts.load() need not raise a `loadingdone` we hear,
// and without this signal nothing re-evaluates the capture's readiness gate and the poster is never
// captured at all. See requestFontFace.
export function notifyFontLoaded(): void {
	setFontEpoch(v => v + 1)
	markGeometryReady()
}

// ── Geometry-write gate ─────────────────────────────────────────────────────────
//
// A text fit measured before the web fonts arrive sees *fallback*-font metrics (see the canvas
// measurer above), so a box fitted on first paint hugs the wrong height. For a bottom/centre-anchored
// field that mis-measured height becomes the anchor, leaving the field a few px off — and because font
// load timing varies, the offset appears intermittently ("the genres field creeps down"). This gate
// flips true once the document's fonts have loaded; AbstractTextImpl.writeGeometry drops geometry
// writes until then, and onGeometryReady re-runs each text element's fit when it opens and on every
// subsequent font load, so the fit that ends up in the store is measured against the real font.
const [geometryReady, setGeometryReady] = createSignal(false)
export { geometryReady }

const geometryReadyCbs = new Set<() => void>()

// Open the geometry gate (the first time) and re-run every registered fit. Driven by document.fonts.
// ready and by every subsequent loadingdone, so a fit that first measured fallback metrics is redone
// once the real font registers — the same way fontEpoch re-runs text wrapping. The callbacks are *not*
// cleared here: each stays registered for its element's lifetime (removed via onCleanup) so a
// late-arriving web font re-fits the box rather than leaving the early, fallback-measured fit in place.
function markGeometryReady(): void {
	if (!geometryReady()) setGeometryReady(true)
	for (const cb of [...geometryReadyCbs]) cb()
	runSettled()  // no text to fit → the fits above never open, so settle here
}

// Run `fn` once the geometry gate is open (fonts loaded), immediately if it already is, and again each
// time the document's fonts finish loading. Runs the callback *after* the gate flag is set, so a fit
// kicked off here passes writeGeometry's gate. Cleanup-bound to the caller's reactive owner.
export function onGeometryReady(fn: () => void): void {
	watchFontLoads()  // arm the gate even if no text has been measured yet
	geometryReadyCbs.add(fn)
	onCleanup(() => { geometryReadyCbs.delete(fn) })
	if (geometryReady()) fn()
}

// ── Fit-settled gate ───────────────────────────────────────────────────────────
//
// A text object's fit lands a frame or more after it mounts (AbstractTextImpl.runFit measures across
// afterRender frames, retrying until the DOM reports a box). Until it does, the element's live
// geometry is still its seed's fallback — width 200 / height 60 / no content offset — because a text
// box is content-derived and its seed carries no measured size. Reading the *laid-out* geometry in
// that window therefore reads defaults, not the layout: the user-mode flatten (UserViewBaker) does
// exactly that, and would collapse a flex container onto unmeasured children, baking the wrong
// absolute positions into the store and dissolving the layout that would have corrected them.
//
// runFit brackets its whole chain (retries included) with beginFit/endFit, so `pending` counts the
// fits still in flight. onGeometrySettled fires only once every one of them has landed *and* the
// font gate is open — i.e. when the live geometry is the real, measured layout.
//
// A signal rather than a plain counter so geometrySettled() can be *read reactively*: EditablePosterSvg
// hides the user-mode object layer until it turns true, so the poster appears measured rather than
// shuffling into place as each fit lands.
const [pending, setPending] = createSignal(0)
const settledCbs = new Set<() => void>()

export function geometrySettled(): boolean { return geometryReady() && pending() === 0 }

export function beginFit(): void { setPending(p => p + 1) }

export function endFit(): void {
	setPending(p => p - 1)
	runSettled()
}

function runSettled(): void {
	if (!geometrySettled()) return
	for (const cb of [...settledCbs]) 
		cb();
}

// Run `fn` once every in-flight text fit has landed and the fonts are loaded, and again whenever the
// geometry re-settles after a later re-fit (a late web font re-runs every fit). Registered before the
// caller's own fits are known to be pending, so the immediate case is deferred a frame: a poster with
// no text at all never opens a fit, and its geometry is settled the moment it mounts.
export function onGeometrySettled(fn: () => void): void {
	watchFontLoads()
	settledCbs.add(fn)
	onCleanup(() => { settledCbs.delete(fn) })
	if (geometrySettled()) afterRender(() => { if (geometrySettled()) fn() })
}

// The same gate as a promise, for callers with no reactive owner to bind a cleanup to — the poster
// capture, which must not rasterise a poster whose text is still mid-fit. Resolves false if nothing
// has settled within timeoutMs, so a poster that never stops re-fitting can't hang the capture.
export function whenGeometrySettled(timeoutMs: number): Promise<boolean> {
	watchFontLoads()
	if (geometrySettled()) return Promise.resolve(true)

	return new Promise<boolean>(resolve => {
		const finish = (settled: boolean) => {
			settledCbs.delete(onSettled)
			clearTimeout(timer)
			resolve(settled)
		}
		const onSettled = () => finish(true)
		const timer = setTimeout(() => finish(false), timeoutMs)
		settledCbs.add(onSettled)
	})
}

// Measure the rendered width of `text` in the given style. `fontSize` overrides style.fontSize
// (used by fitFontSize while binary-searching a size). The CSS font shorthand carries
// style/weight/size/family; letter-spacing isn't part of the shorthand and the canvas measurer
// ignores it, so we add it back manually (one gap after each character, matching SVG's
// getComputedTextLength). italic is reflected via the font shorthand's style slot.
export function measureWidth(text: string, style: Style | undefined, fontSize?: number): number {
	watchFontLoads()
	fontEpoch()  // subscribe: re-measure when web fonts finish loading (see fontEpoch above)
	if (!_measurer) _measurer = document.createElement('canvas').getContext('2d')!
	const fs        = (fontSize ?? style?.fontSize ?? 16) as number
	const family    = (style?.fontFamily    ?? 'sans-serif') as string
	const weight    = (style?.fontWeight    ?? 400) as number | string
	const fontStyle = (style?.fontStyle     ?? 'normal') as string
	const letterSp  = (style?.letterSpacing ?? 0) as number
	_measurer.font = `${fontStyle} ${weight} ${fs}px ${family}`
	let width = _measurer.measureText(text).width
	if (letterSp && text.length) width += letterSp * text.length
	return width
}

// Word-wrap `text` to fit within `maxWidth` using canvas measurement.
// Respects existing \n line breaks. Long single words are left unbroken.
export function wrapText(text: string, maxWidth: number, style?: Style): string[] {
	const lines: string[] = []
	for (const para of text.split('\n')) {
		let line = ''
		for (const word of para.split(' ')) {
			const test = line ? `${line} ${word}` : word
			if (line && measureWidth(test, style) > maxWidth) {
				lines.push(line)
				line = word
			} else {
				line = test
			}
		}
		lines.push(line)
	}
	while (lines.length > 1 && lines[lines.length - 1] === '') lines.pop()
	return lines
}

// ── Position-based wrapping (plain text) ────────────────────────────────────────
//
// Plain text stores explicit *soft wrap positions* — character offsets into the content where a
// line break is inserted — rather than a wrap width. Combined with the user's hard \n breaks these
// fully determine the rendered lines with no font measurement, so the layout is deterministic
// (independent of when web fonts load, and reproducible during SSR/export).

// Split `text` into display lines at each hard \n and each soft offset in `positions`. A soft offset
// `p` breaks the line *before* the character at index `p`; the single space that greedy wrapping
// drops between the two words is trimmed from the end of the preceding line (see wrapText). No
// measurement.
export function wrapAtPositions(text: string, positions: number[]): string[] {
	const breaks = new Set(positions)
	const lines: string[] = []
	let line = ''
	for (let i = 0; i < text.length; i++) {
		if (breaks.has(i)) { lines.push(line.replace(/ +$/, '')); line = '' }
		const ch = text[i]
		if (ch === '\n') { lines.push(line); line = '' }
		else line += ch
	}
	lines.push(line)
	return lines
}

// The inverse of wrapAtPositions for a target width: greedy-wrap `text` at `maxWidth` (the same
// algorithm as wrapText) but emit the absolute character index of the first word of each
// soft-broken line instead of the lines themselves. Hard \n breaks are paragraph boundaries and are
// *not* recorded (they already live in the content). Invariant:
//   wrapAtPositions(text, wrapPositionsForWidth(text, w, s)) === wrapText(text, w, s)
// This is the one place a width is measured; the result is what gets stored, the width is not.
export function wrapPositionsForWidth(text: string, maxWidth: number, style?: Style): number[] {
	const positions: number[] = []
	let base = 0  // absolute index of the start of the current paragraph
	for (const para of text.split('\n')) {
		let line   = ''
		let cursor = base  // absolute index of the word about to be considered
		for (const word of para.split(' ')) {
			const test = line ? `${line} ${word}` : word
			if (line && measureWidth(test, style) > maxWidth) {
				positions.push(cursor)   // break before this word
				line = word
			} else {
				line = test
			}
			cursor += word.length + 1    // advance past the word and its trailing space
		}
		base += para.length + 1          // advance past the paragraph and its trailing \n
	}
	return positions
}

